import { soundService } from '../services/soundService';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { Mic, ChevronRight, ArrowLeft, Star, SkipForward, Globe, CheckCircle2, XCircle, Volume2, Brain, Trophy, ThumbsUp, RotateCcw, GraduationCap, Loader2, AlertCircle, ChevronDown, Plus, Minus, Square, Play, X, Edit3, Trash2, ChevronLeft, Check, ArrowRight } from 'lucide-react';
import { storageService } from '../services/storageService';
import { textToSpeechService } from '../services/textToSpeechService';
import { offlineTtsEngine } from '../services/offlineTtsEngine';
import { groqService } from '../services/groqService';
import { notesDeckManager } from '../services/notesDeckManager';
import { exerciseProgressService } from '../services/exerciseProgressService';
import { supabase } from '../services/supabaseClient';
import { dbManager } from '../services/db_manager';
import { useAuth } from '../contexts/AuthContext';
import { useStreakCounter } from '../hooks/useStreakCounter';
import { getLanguageCode, getLanguageName, LANG_LABEL_MAP as GLOBAL_LANG_LABEL_MAP } from '../services/languageConstants';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { statsService } from '../services/statsService';
import { activeStudyTracker } from '../services/activeStudyTrackerService';
import { useTranslation } from 'react-i18next';
import { tourService } from '../services/tourService';
import { flashcardManager } from '../services/flashcardManager';
import { streakService } from '../services/streakService';
import { getPronunciationExercises } from '../services/exercises';
import { getLocalISOString } from '../utils/timezoneUtils';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import { AudioWaveformComparator } from '../components/AudioWaveformComparator';
import { getPronunciationAudioUrl, loadPronunciationArrayBuffer } from '../services/pronunciationAudioService';
import { normalizeNumbersInText } from '../utils/numberPronunciationUtils';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { AnimatedIcon } from '../components/AnimatedIcon';
import T from '../components/T';
import i18nInstance from '../i18n';


// Firebase removed - Using R2

interface Card {
  id: string;
  text: string;
  lang: string;
  translation?: string;
}

interface Deck {
  id: string;
  name: string;
  lang: string;
  cards: Card[];
  type: 'course' | 'personal';
  level?: string;
}

const SUCCESS_SOUND_URL = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/pronuncia-cards/assets/success.ogg';

// Mapeamento de IDs internos para pastas no Banco
const LANG_TO_FOLDER: Record<string, string> = {
  'en-US': 'ingles',
  'fr-FR': 'frances',
  'de-DE': 'alemao',
  'pt-BR': 'portugues',
  'it-IT': 'italiano',
  'es-ES': 'espanhol',
  'nl-NL': 'holandes',
  'ja-JP': 'japones',
  'ru-RU': 'russo',
  'zh-CN': 'chines'
};

const LANGUAGES = [
  { id: 'en-US', name: 'Inglês', flag: '🇺🇸' },
  { id: 'fr-FR', name: 'Francês', flag: '🇫🇷' },
  { id: 'de-DE', name: 'Alemão', flag: '🇩🇪' },
  { id: 'pt-BR', name: 'Português', flag: '🇧🇷' },
  { id: 'it-IT', name: 'Italiano', flag: '🇮🇹' },
  { id: 'es-ES', name: 'Espanhol', flag: '🇪🇸' },
  { id: 'nl-NL', name: 'Holandês', flag: '🇳🇱' },
  { id: 'ja-JP', name: 'Japonês', flag: '🇯🇵' },
  { id: 'ru-RU', name: 'Russo', flag: '🇷🇺' },
  { id: 'zh-CN', name: 'Chinês', flag: '🇨🇳' }
];

const NATIVE_LANGUAGES = [
  { name: 'Alemão', flag: '🇩🇪', folder: 'alemao' },
  { name: 'Chinês', flag: '🇨🇳', folder: 'chines' },
  { name: 'Espanhol', flag: '🇪🇸', folder: 'espanhol' },
  { name: 'Francês', flag: '🇫🇷', folder: 'frances' },
  { name: 'Holandês', flag: '🇳🇱', folder: 'holandes' },
  { name: 'Inglês', flag: '🇺🇸', folder: 'ingles' },
  { name: 'Italiano', flag: '🇮🇹', folder: 'italiano' },
  { name: 'Japonês', flag: '🇯🇵', folder: 'japones' },
  { name: 'Português', flag: '🇧🇷', folder: 'portugues' },
  { name: 'Russo', flag: '🇷🇺', folder: 'russo' }
];

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

// Using LANG_LABEL_MAP from languageConstants
const LANG_LABEL_MAP = GLOBAL_LANG_LABEL_MAP;

// Comprehensive multi-language name → BCP-47 locale mapping.
// Covers all 10 supported UI languages so deck tagging works regardless of app language.
const LANGUAGE_NAME_TO_LOCALE: Record<string, string> = {
  // Portuguese
  'inglês': 'en-US', 'francês': 'fr-FR', 'alemão': 'de-DE', 'português': 'pt-BR',
  'italiano': 'it-IT', 'espanhol': 'es-ES', 'holandês': 'nl-NL', 'japonês': 'ja-JP',
  'russo': 'ru-RU', 'chinês': 'zh-CN', 'mandarim': 'zh-CN',
  // English
  'english': 'en-US', 'french': 'fr-FR', 'german': 'de-DE', 'portuguese': 'pt-BR',
  'italian': 'it-IT', 'spanish': 'es-ES', 'dutch': 'nl-NL', 'japanese': 'ja-JP',
  'russian': 'ru-RU', 'chinese': 'zh-CN', 'mandarin': 'zh-CN',
  // Spanish
  'inglés': 'en-US', 'francés': 'fr-FR', 'alemán': 'de-DE', 'portugués': 'pt-BR',
  'español': 'es-ES', 'holandés': 'nl-NL', 'japonés': 'ja-JP',
  'ruso': 'ru-RU', 'chino': 'zh-CN',
  // French
  'anglais': 'en-US', 'français': 'fr-FR', 'allemand': 'de-DE', 'portugais': 'pt-BR',
  'italien': 'it-IT', 'espagnol': 'es-ES', 'néerlandais': 'nl-NL', 'japonais': 'ja-JP',
  'russe': 'ru-RU', 'chinois': 'zh-CN',
  // Italian
  'inglese': 'en-US', 'francese': 'fr-FR', 'tedesco': 'de-DE', 'portoghese': 'pt-BR',
  'spagnolo': 'es-ES', 'olandese': 'nl-NL', 'giapponese': 'ja-JP', 'cinese': 'zh-CN',
  // German
  'englisch': 'en-US', 'französisch': 'fr-FR', 'deutsch': 'de-DE', 'portugiesisch': 'pt-BR',
  'italienisch': 'it-IT', 'spanisch': 'es-ES', 'niederländisch': 'nl-NL', 'japanisch': 'ja-JP',
  'russisch': 'ru-RU', 'chinesisch': 'zh-CN',
  // Japanese
  '英語': 'en-US', 'フランス語': 'fr-FR', 'ドイツ語': 'de-DE', 'ポルトガル語': 'pt-BR',
  'イタリア語': 'it-IT', 'スペイン語': 'es-ES', 'オランダ語': 'nl-NL', '日本語': 'ja-JP',
  'ロシア語': 'ru-RU', '中国語': 'zh-CN',
  // Chinese (simplified)
  '英语': 'en-US', '法语': 'fr-FR', '德语': 'de-DE', '葡萄牙语': 'pt-BR',
  '意大利语': 'it-IT', '西班牙语': 'es-ES', '荷兰语': 'nl-NL', '日语': 'ja-JP',
  '俄语': 'ru-RU', '中文': 'zh-CN',
  // Russian
  'английский': 'en-US', 'французский': 'fr-FR', 'немецкий': 'de-DE', 'португальский': 'pt-BR',
  'итальянский': 'it-IT', 'испанский': 'es-ES', 'нидерландский': 'nl-NL', 'голландский': 'nl-NL',
  'японский': 'ja-JP', 'русский': 'ru-RU', 'китайский': 'zh-CN',
  // Hungarian
  'angol': 'en-US', 'francia': 'fr-FR', 'német': 'de-DE', 'portugál': 'pt-BR',
  'olasz': 'it-IT', 'spanyol': 'es-ES', 'holland': 'nl-NL', 'japán': 'ja-JP',
  'orosz': 'ru-RU', 'kínai': 'zh-CN',
};

/**
 * Resolves a language name, code, or locale to a proper BCP-47 locale for TTS.
 * Works with language names in any of the 10 supported UI languages.
 */
// Ordem aleatória a cada abertura do baralho (nunca a mesma sequência fixa)
const shuffleCards = <T,>(cards: T[]): T[] => {
  const arr = [...cards];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const resolveDeckLocale = (langOrName: string): string => {
  if (!langOrName) return 'en-US';
  // Already a valid BCP-47 locale (e.g., "en-US", "fr-FR")
  if (/^[a-z]{2}-[A-Z]{2}$/.test(langOrName)) return langOrName;
  // Short ISO code (e.g., "en", "fr")
  const shortCode = langOrName.toLowerCase().split(/[-_]/)[0];
  const SHORT_ISO: Record<string, string> = {
    'en': 'en-US', 'fr': 'fr-FR', 'de': 'de-DE', 'pt': 'pt-BR',
    'it': 'it-IT', 'es': 'es-ES', 'nl': 'nl-NL', 'ja': 'ja-JP',
    'ru': 'ru-RU', 'zh': 'zh-CN'
  };
  if (SHORT_ISO[shortCode] && langOrName.length <= 5) return SHORT_ISO[shortCode];
  // Language name lookup (case-insensitive)
  const normalized = langOrName.toLowerCase().trim();
  if (LANGUAGE_NAME_TO_LOCALE[normalized]) return LANGUAGE_NAME_TO_LOCALE[normalized];
  // Partial match fallback
  for (const [name, locale] of Object.entries(LANGUAGE_NAME_TO_LOCALE)) {
    if (normalized.startsWith(name) || name.startsWith(normalized)) return locale;
  }
  return 'en-US';
};


const getLevenshteinDistance = (a: string, b: string): number => {
  const matrix: number[][] = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

const getSimilarity = (s1: string, s2: string): number => {
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  const longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  return (longerLength - getLevenshteinDistance(longer, shorter)) / parseFloat(longerLength.toString());
};

const ConfirmationModal: React.FC<{
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  isDanger?: boolean;
}> = ({ isOpen, title, message, onConfirm, onCancel, confirmLabel = 'Confirmar', isDanger = false }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[85vw] max-w-md animate-in zoom-in-95 duration-200">
        <div className={`w-12 h-12 rounded-2xl mb-6 flex items-center justify-center ${isDanger ? 'bg-tint-rose text-bad dark:bg-tint-rose/20' : 'bg-primary/10 text-primary'}`}>
          <AlertCircle size={24} />
        </div>
        <h3 className="text-lg font-semibold dark:text-ink mb-2">{title}</h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-8 leading-relaxed font-medium">{message}</p>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={onCancel} className="text-[13px] font-medium h-12 bg-tint-stone/60 text-muted hover:text-ink dark:text-slate-400 rounded-full hover:bg-tint-stone transition-all">Cancelar</button>
          <button onClick={onConfirm} className={`py-4 ${isDanger ? 'bg-bad' : 'bg-ink'} text-white rounded-2xl font-normal text-[10px] uppercase font-mono tracking-[0.14em] hover:opacity-90 transition-all active:scale-95`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const getTranslatedDeckName = (name: string, t: any): string => {
  if (!name) return '';
  const safeT = (key: string, fallback: string) => {
    const res = typeof t === 'function' ? t(key) : key;
    return res && res !== key ? res : fallback;
  };
  const lowerName = name.toLowerCase().trim();
  if (lowerName.includes('ingles') || lowerName === 'english') return safeT('languages.en', 'Inglês');
  if (lowerName.includes('frances') || lowerName === 'french') return safeT('languages.fr', 'Francês');
  if (lowerName.includes('alemao') || lowerName === 'german') return safeT('languages.de', 'Alemão');
  if (lowerName.includes('espanhol') || lowerName === 'spanish') return safeT('languages.es', 'Espanhol');
  if (lowerName.includes('italiano') || lowerName === 'italian') return safeT('languages.it', 'Italiano');
  if (lowerName.includes('portugues') || lowerName === 'portuguese') return safeT('languages.pt', 'Português');
  if (lowerName.includes('russo') || lowerName === 'russian') return safeT('languages.ru', 'Russo');
  if (lowerName.includes('japones') || lowerName === 'japanese') return safeT('languages.ja', 'Japonês');
  if (lowerName.includes('chines') || lowerName === 'chinese') return safeT('languages.zh', 'Chinês');
  if (lowerName.includes('holandes') || lowerName === 'dutch') return safeT('languages.nl', 'Holandês');
  return name;
};

const PronunciationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { langCode: routeLang, level: routeLevel } = useParams<{ langCode?: string; level?: string }>();
  const location = useLocation();
  const isGuide = location.pathname.includes('/guide') || searchParams.get('from') === 'guide';
  const { user } = useAuth();
  const { profile, refreshProfile } = useData();
  const { targetLanguage } = useTheme();

  const CODE_TO_LOCALE: Record<string, string> = {
    'en': 'en-US', 'fr': 'fr-FR', 'de': 'de-DE', 'pt': 'pt-BR',
    'it': 'it-IT', 'es': 'es-ES', 'nl': 'nl-NL', 'ja': 'ja-JP',
    'ru': 'ru-RU', 'zh': 'zh-CN'
  };

  const initialRawLang = routeLang || searchParams.get('lang');
  const initialResolvedLang = initialRawLang
    ? (CODE_TO_LOCALE[getLanguageCode(initialRawLang)] || CODE_TO_LOCALE[initialRawLang.toLowerCase()] || initialRawLang)
    : 'en-US';
  const initialResolvedLevel = (routeLevel || searchParams.get('level') || 'A1').toUpperCase();

  const [view, setView] = useState<'languages' | 'levels' | 'practice'>(() => {
    if (routeLang || searchParams.get('lang') || isGuide) return 'practice';
    return 'practice';
  });
  useStreakCounter(view === 'practice');
  const [selectedLang, setSelectedLang] = useState<string | null>(initialResolvedLang);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(initialResolvedLevel);
  const [selectedDeck, setSelectedDeck] = useState<Deck | null>(null);

  const [activeTab, setActiveTab] = useState<'geral' | 'focado'>('geral');

  // Deck metadata (names and favorites)
  const [deckToDelete, setDeckToDelete] = useState<string | null>(null);

  const [deckMeta, setDeckMeta] = useState<Record<string, { name?: string, isFavorite?: boolean }>>(() => {
    const saved = localStorage.getItem('libritui_pronunciation_deck_meta');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('libritui_pronunciation_deck_meta', JSON.stringify(deckMeta));
  }, [deckMeta]);

  const toggleFavorite = (lang: string) => {
    setDeckMeta(prev => ({
      ...prev,
      [lang]: { ...prev[lang], isFavorite: !prev[lang]?.isFavorite }
    }));
  };

  const renameDeck = (lang: string) => {
    const currentName = deckMeta[lang]?.name || LANG_LABEL_MAP[lang] || lang;
    const newName = prompt('Editar nome do baralho:', currentName);
    if (newName && newName.trim()) {
      setDeckMeta(prev => ({
        ...prev,
        [lang]: { ...prev[lang], name: newName.trim() }
      }));
    }
  };

  const deleteDeck = async (deckId: string) => {
    // deckId is like 'personal-Inglês', we need just 'Inglês'
    const categoryOrLang = deckId.startsWith('personal-') ? deckId.replace('personal-', '') : deckId;
    notesDeckManager.deletePronunciationDeck(categoryOrLang); // Background
    setPersonalDecks(prev => prev.filter(d => d.id !== deckId));
    setDeckToDelete(null);
    alert("Excluído com sucesso!");
  };

  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const { t, i18n } = useTranslation();
  const safeT = (key: string, fallback: string, options?: any) => {
    const res = t(key, { defaultValue: fallback, ...options });
    return (res === key || !res) ? fallback : res;
  };
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [lastTranscript, setLastTranscript] = useState('');
  const [feedback, setFeedback] = useState<'success' | 'partial' | 'error' | null>(null);
  const cardAttemptsRef = useRef<{status: string[], transcript: string[]}>({ status: [], transcript: [] });
  
  // Garantia Absoluta via ID da Frase
  useEffect(() => {
    if (selectedDeck && selectedDeck.cards[currentCardIndex]) {
      cardAttemptsRef.current = { status: [], transcript: [] };
    }
  }, [currentCardIndex, selectedDeck]);

  // Active study tracker initialization & unmount flush for /pronunciation
  useEffect(() => {
    const rawLang = selectedDeck?.lang || profile?.learning_language;
    const clean = rawLang ? rawLang.split('-')[0].toLowerCase() : null;

    if (user?.id && clean && clean !== 'all') {
      activeStudyTracker.setUserContext(user.id, clean);
    }
    return () => {
      activeStudyTracker.flush();
    };
  }, [user?.id, selectedDeck?.lang, profile?.learning_language]);

  const [personalDecks, setPersonalDecks] = useState<Deck[]>([]);

  const [editingDeck, setEditingDeck] = useState<Deck | null>(null);
  const [selectedCardsForDeletion, setSelectedCardsForDeletion] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [editingVisibleCount, setEditingVisibleCount] = useState(20);
  const [showConfirmBatchDelete, setShowConfirmBatchDelete] = useState(false);

  const handleEditDeck = (e: React.MouseEvent, deck: Deck) => {
    e.stopPropagation();
    setEditingDeck(deck);
    setIsSelectionMode(false);
    setSelectedCardsForDeletion(new Set());
    setEditingVisibleCount(20);
  };

  const handleScrollEditView = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop <= e.currentTarget.clientHeight + 100;
    if (bottom && editingDeck && editingVisibleCount < editingDeck.cards.length) {
      setEditingVisibleCount(prev => prev + 20);
    }
  };

  const handleBatchDelete = () => {
    const idsToDelete = Array.from(selectedCardsForDeletion);
    if (idsToDelete.length === 0) return;

    flashcardManager.enqueueDeletion(idsToDelete);

    setPersonalDecks(prev => prev.map(d => {
       if (d.id === editingDeck?.id) {
          return { ...d, cards: d.cards.filter(c => !idsToDelete.includes(String(c.id))) };
       }
       return d;
    }));
    
    if (editingDeck) {
       setEditingDeck(prev => prev ? { ...prev, cards: prev.cards.filter(c => !idsToDelete.includes(String(c.id))) } : null);
    }
    
    setShowConfirmBatchDelete(false);
    setIsSelectionMode(false);
    setSelectedCardsForDeletion(new Set());
  };

  const [userAudioUrl, setUserAudioUrl] = useState<string | null>(null);
  const [userAudioBlob, setUserAudioBlob] = useState<Blob | null>(null);
  const [isUserAudioPlaying, setIsUserAudioPlaying] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const userAudioElementRef = useRef<HTMLAudioElement | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const [ttsRate, setTtsRate] = useState(0.9);
  const [isTtsPlaying, setIsTtsPlaying] = useState(false);
  const [aiFeedback, setAiFeedback] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const r2AudioCtxRef = useRef<AudioContext | null>(null);
  const r2AudioSourceRef = useRef<AudioBufferSourceNode | null>(null);

  const [timeLeft, setTimeLeft] = useState(15);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingFeedback, setIsProcessingFeedback] = useState(false);
  const [isNextReady, setIsNextReady] = useState(false);
  const [phonetic, setPhonetic] = useState<string | null>(null);
  const consecutiveErrorsRef = useRef<Record<number, number>>({});

  const unifiedDict = useUnifiedDictionary();

  const handleWordClick = async (e: React.MouseEvent<HTMLSpanElement>, word: string, isSwapped: boolean = false) => {
    e.stopPropagation();
    const cleanWord = word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
    if (!cleanWord) return;

    const userUiLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
    const exerciseLang = resolveDeckLocale(selectedDeck?.lang || selectedLang || 'en').split('-')[0].toLowerCase();
    const sourceLangCode = isSwapped ? userUiLang : exerciseLang;
    const targetLangCode = isSwapped ? exerciseLang : userUiLang;

    unifiedDict.handleWordClick(e, cleanWord, sourceLangCode, targetLangCode);
  };

  const renderClickableText = (text: string, isSwapped: boolean = false) => {
    const parts = text.split(/(\s+)/);
    return parts.map((part, idx) => {
      const hasLetters = /[a-zA-ZÀ-ÿ\w]/.test(part);
      if (hasLetters) {
        return (
          <span 
            key={idx}
            onClick={(e) => handleWordClick(e, part, isSwapped)}
            className="hover:text-primary hover:underline cursor-pointer transition-colors relative inline-block"
          >
            {part}
          </span>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const isManualStopRef = useRef(false);
  const isFinalizingRef = useRef(false);
  const lastTranscriptRef = useRef('');
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recognitionRef = useRef<any>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wakeLockRef = useRef<any>(null);

  useEffect(() => {
    const handleTour = () => {
      if (tourService.isActive() && tourService.getPhase() === 'PRONUNCIATION') {
        if (view === 'languages') {
          tourService.createDriver([
            {
              element: '#tour-pronunciation-deck',
              popover: {
                title: 'Treino de Fala',
                description: 'Aqui você pratica sua dicção e perde o medo de falar! Clique em um baralho para começarmos.',
                showButtons: []
              },
              onHighlighted: (el) => {
                 if (el) el.style.pointerEvents = 'auto';
              }
            }
          ]);
        } else if (view === 'practice') {
          tourService.setPhase('GOTO_WRITING');
          tourService.createDriver([
            {
              element: '#tour-pronunciation-back',
              popover: {
                title: 'Ajuste e Fale!',
                description: 'Aqui você ouve a pronúncia e grava sua voz. Volte ao Hub para irmos à próxima ferramenta: Escrita!',
                showButtons: []
              }
            }
          ]);
        }
      }
    };

    setTimeout(handleTour, 800);
    window.addEventListener('libritui-tour-started', handleTour);
    return () => window.removeEventListener('libritui-tour-started', handleTour);
  }, [view, personalDecks]);

  const loadPronunciationDecks = async () => {
    try {
      // 1. Load Local Notes (Immediate & Guest fallback)
      const localNotes = notesDeckManager.getPronunciationNotes();

      // 2. Load Cloud Notes (If authenticated)
      let cloudNotes: any[] = [];
      if (user && !user.isGuest) {
        const cloudData = await dbManager.from('user_pronunciation', user.id).select();
        if (Array.isArray(cloudData)) {
          cloudNotes = cloudData;
          cloudNotes.sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        }
      }

      // 3. Merge & Deduplicate
      // Use "original|language" as a key for deduplication
      const mergedMap = new Map<string, any>();

      // Local has priority for current session feel
      localNotes.forEach(n => {
        const key = `${n.original.trim().toLowerCase()}|${n.langCode}`;
        mergedMap.set(key, { ...n, source: 'local' });
      });

      // Cloud adds anything missing or updates
      cloudNotes.forEach(n => {
        const key = `${n.original.trim().toLowerCase()}|${n.lang_code || n.language}`;
        if (!mergedMap.has(key)) {
          mergedMap.set(key, {
            id: n.id,
            original: n.original,
            translation: n.translation,
            langCode: n.lang_code || n.language,
            category: n.category,
            source: 'cloud'
          });
        }
      });

      const mergedNotes = Array.from(mergedMap.values());
      const grouped = notesDeckManager.groupByLang(mergedNotes);

      const personal = Object.entries(grouped).map(([langOrCat, cards]) => {
        // Resolve proper BCP-47 locale for TTS from cards' langCode or deck name
        const firstCardLang = cards[0]?.langCode;
        const resolvedLang = resolveDeckLocale(firstCardLang || langOrCat);

        return {
          id: `personal-${langOrCat}`,
          name: langOrCat.length > 5 ? langOrCat : (LANG_LABEL_MAP[langOrCat] || langOrCat),
          lang: resolvedLang,
          cards: cards.slice(0, 50).map((c: any) => ({
            id: c.id.toString(),
            text: c.original,
            lang: resolveDeckLocale(c.langCode || langOrCat),
            translation: c.translation
          })),
          type: 'personal' as const
        };
      });

      setPersonalDecks(personal);
    } catch (err) {
      console.error("Falha ao carregar decks pessoais:", err);
    }
  };

  useEffect(() => {
    // Initial load
    loadPronunciationDecks();

    // Re-load whenever the brain icon saves new notes (same-page SPA event)
    const onUpdate = () => loadPronunciationDecks();
    window.addEventListener('notesDeckUpdated', onUpdate);

    return () => {
      window.removeEventListener('notesDeckUpdated', onUpdate);
      
      // 1. Audio and Speech Synthesis
      try {
        window.speechSynthesis.cancel();
      } catch (e) {}
      try {
        textToSpeechService.stop();
      } catch (e) {}
      if (userAudioElementRef.current) {
        try {
          userAudioElementRef.current.pause();
        } catch (e) {}
        userAudioElementRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {}
        mediaRecorderRef.current = null;
      }
      if (audioRef.current) {
        try {
          audioRef.current.pause();
        } catch (e) {}
        audioRef.current = null;
      }
      
      // 2. Speech Recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
          recognitionRef.current.stop();
        } catch (e) {}
        recognitionRef.current = null;
      }
      
      // 3. Timers and Intervals
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
      
      // 4. Wake Lock Release
      if (wakeLockRef.current) {
        try {
          wakeLockRef.current.release();
        } catch (e) {}
        wakeLockRef.current = null;
      }
    };
  }, []);



  const playSuccessSound = async () => {
    try {
      const audio = new Audio(SUCCESS_SOUND_URL);
      audio.volume = 0.1;
      await audio.play();
    } catch (e) {
      console.debug("[Pronunciation] Audio playback suppressed.");
    }
  };

  /**
   * Função para buscar dados de pronúncia do R2.
   */
  const fetchPronunciationData = async (language: string, level: string) => {
    try {
      const data = await getPronunciationExercises(language, level);
      return data || [];
    } catch (error) {
      console.error("Erro na busca de pronúncia do R2:", error);
      throw error;
    }
  };

  const handleLangSelect = (langId: string) => {
    setSelectedLang(langId);
    setFetchError(null);
    setView('levels');
  };

  const handleLevelSelect = async (level: string, langOverride?: string) => {
    let rawLang = langOverride || selectedLang;
    if (!rawLang) return;

    // Normalizar para o formato esperado pelo LANGUAGES ('en' -> 'en-US', etc.)
    const CODE_TO_LOCALE: Record<string, string> = {
      'en': 'en-US', 'fr': 'fr-FR', 'de': 'de-DE', 'pt': 'pt-BR',
      'it': 'it-IT', 'es': 'es-ES', 'nl': 'nl-NL', 'ja': 'ja-JP',
      'ru': 'ru-RU', 'zh': 'zh-CN'
    };
    const activeLang = CODE_TO_LOCALE[rawLang.toLowerCase()] || rawLang;

    const targetLevel = level.toUpperCase();
    setSelectedLang(activeLang);
    setSelectedLevel(targetLevel);
    setIsLoadingCards(true);
    setFetchError(null);

    const CODE_TO_FOLDER: Record<string, string> = {
      'en': 'ingles', 'fr': 'frances', 'de': 'alemao', 'pt': 'portugues',
      'it': 'italiano', 'es': 'espanhol', 'nl': 'holandes', 'ja': 'japones',
      'ru': 'russo', 'zh': 'chines'
    };
    const studyFolder = LANG_TO_FOLDER[activeLang] || CODE_TO_FOLDER[activeLang.toLowerCase().split('-')[0]] || 'ingles';
    const nativeFolder = CODE_TO_FOLDER[targetLanguage.split('-')[0].toLowerCase()] || 'portugues';

    try {
      // Sincronização via R2
      const studyCards: any[] = await fetchPronunciationData(studyFolder, targetLevel);

      let nativeCards: any[] = [];
      try {
        if (nativeFolder !== studyFolder) {
          nativeCards = await fetchPronunciationData(nativeFolder, targetLevel);
        }
      } catch (e) {
        console.warn(`[Pronunciation] Tradução para ${nativeFolder} não encontrada.`);
      }

      const langData = LANGUAGES.find(l => l.id === activeLang) || LANGUAGES.find(l => l.id.startsWith(activeLang.split('-')[0])) || LANGUAGES[0];

      const newDeck: Deck = {
        id: `course-${activeLang}-${targetLevel}`,
        name: `${langData?.name || activeLang} - Nível ${targetLevel}`,
        lang: activeLang,
        level: targetLevel,
        type: 'course',
        cards: studyCards.map((item: any, i: number) => {
          const nativeMatch = nativeCards.find(nc => String(nc.id) === String(item.id));
          return {
            id: String(item.id || i),
            text: item.phrase || item.text || (typeof item === 'string' ? item : ''),
            translation: nativeMatch?.phrase || item.translation || '',
            lang: activeLang
          };
        })
      };

      // Embaralhar para sempre exibir aleatoriamente
      const shuffledCards = [...newDeck.cards];
      for (let i = shuffledCards.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledCards[i], shuffledCards[j]] = [shuffledCards[j], shuffledCards[i]];
      }
      newDeck.cards = shuffledCards;

      setCurrentCardIndex(0);
      setSelectedDeck(newDeck);
      setFeedback(null);
      setLastTranscript('');
      setView('practice');
    } catch (err: any) {
      console.error("[Pronunciation Load Error]:", err);
      setFetchError(err.message || 'Erro ao sincronizar material de estudo.');
    } finally {
      setIsLoadingCards(false);
    }
  };

  // Auto-start or sync deck when route params or selectors change
  useEffect(() => {
    const rawLang = routeLang || searchParams.get('lang');
    const rawLevel = routeLevel || searchParams.get('level');

    if (rawLang) {
      const resolved = CODE_TO_LOCALE[getLanguageCode(rawLang)] || CODE_TO_LOCALE[rawLang.toLowerCase()] || rawLang;
      setSelectedLang(resolved);
      const lvl = (rawLevel || selectedLevel || 'A1').toUpperCase();
      setSelectedLevel(lvl);
      handleLevelSelect(lvl, resolved);
    } else if (selectedLang && selectedLevel && !selectedDeck && activeTab === 'geral') {
      handleLevelSelect(selectedLevel, selectedLang);
    }
  }, [routeLang, routeLevel, searchParams]);

  const resetExerciseState = () => {
    // 1. Audio and Speech reset
    try { window.speechSynthesis.cancel(); } catch (e) {}
    try { textToSpeechService.stop(); } catch (e) {}

    if (userAudioElementRef.current) {
      try { userAudioElementRef.current.pause(); } catch (e) {}
      userAudioElementRef.current = null;
    }

    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (e) {}
      audioRef.current = null;
    }

    if (r2AudioSourceRef.current) {
      try { r2AudioSourceRef.current.stop(); } catch (e) {}
      r2AudioSourceRef.current = null;
    }
    if (r2AudioCtxRef.current) {
      try { r2AudioCtxRef.current.close(); } catch (e) {}
      r2AudioCtxRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
      mediaRecorderRef.current = null;
    }
    audioChunksRef.current = [];

    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
        recognitionRef.current.stop();
      } catch (e) {}
      recognitionRef.current = null;
    }

    // 2. Clear timers
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);

    // 3. Reset Blob URLs
    setUserAudioUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setUserAudioBlob(null);

    // 4. Reset states
    setIsNextReady(false);
    setFeedback(null);
    setAiFeedback(null);
    setLastTranscript('');
    lastTranscriptRef.current = '';
    setIsUserAudioPlaying(false);
    setIsTtsPlaying(false);
    setIsListening(false);
    setIsProcessing(false);
    setIsProcessingFeedback(false);
    setIsTransitioning(false);
    setIsRequestingPermission(false);
    setPhonetic(null);
    releaseWakeLock();

    // 5. Reset refs
    isFinalizingRef.current = false;
    isManualStopRef.current = true;
  };

  const performHardReset = () => {
    resetExerciseState();
  };

  const handleGoBack = async () => {
    savePronunciationHistory();
    // Use Hard Reset as an emergency hatch
    performHardReset();

    if (isGuide) {
      navigate('/');
      return;
    }

    if (tourService.isActive() && tourService.getPhase() === 'GOTO_WRITING') {
       navigate('/study-hub');
       return;
    }

    if (isGuide) {
      navigate('/');
      return;
    }

    goBackOr(navigate, '/study-hub');
  };


  // Reset state and fetch phonetics when current card changes
  useEffect(() => {
    resetExerciseState();
    
    if (selectedDeck && selectedDeck.cards[currentCardIndex]) {
      const text = selectedDeck.cards[currentCardIndex].text;
      fetchPhonetics(text);
    }
  }, [selectedDeck, currentCardIndex]);

  const fetchPhonetics = async (text: string) => {
    setPhonetic(null);
    try {
      const wordToSearch = text.split(' ')[0].replace(/[.,\/#!$%\^&\*;:{}=\-_~()?]/g, "");
      if (!wordToSearch) return;

      const deckLangShort = (selectedDeck?.lang || 'en-US').split('-')[0].toLowerCase();
      // dictionaryapi.dev primarily supports English ('en'). Calling it for non-English (fr, de, pt, etc.) causes CORS / 522 network errors on their server.
      if (deckLangShort !== 'en') return;

      const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${wordToSearch}`);
      if (!response.ok) return;

      const data = await response.json();
      if (data && data[0]) {
        const text = data[0].phonetic || (data[0].phonetics && data[0].phonetics.find((p: any) => p.text)?.text);
        if (text) setPhonetic(text);
      }
    } catch (e) {
      // Quietly ignore fetch failure
    }
  };

  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
      }
    } catch (err) {
      console.warn('Wake Lock error:', err);
    }
  };

  const releaseWakeLock = async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (err) {
      console.warn('Wake Lock release error:', err);
    }
  };

  const stopListening = async () => {
    if (!isListening && !recognitionRef.current) return;

    isManualStopRef.current = true;
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.warn("Recognition stop error:", e);
      }
      recognitionRef.current = null;
    }
    stopListeningState();

    // Process current transcript from Ref to ensure we don't lose the buffer
    const transcriptToValidate = lastTranscriptRef.current.trim();
    if (transcriptToValidate && !isFinalizingRef.current) {
      try {
        const { whisperSTTService } = await import('../services/WhisperSTTService');
        const isWhisperReady = await whisperSTTService.isModelCached();
        if (isWhisperReady) {
          console.log('[STT Engine] Usado com sucesso: Whisper Tiny (Local R2)');
        } else {
          console.log('[STT Engine] Usado: Fallback Nativo do Chrome');
        }
      } catch (e) {
        console.log('[STT Engine] Usado: Fallback Nativo do Chrome');
      }
      validate(transcriptToValidate);
    }
  };

  const playUserAudio = () => {
    if (!userAudioUrl) return;

    window.speechSynthesis.cancel();
    setIsTtsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    if (userAudioElementRef.current) {
      userAudioElementRef.current.pause();
      const isSame = userAudioElementRef.current.src === userAudioUrl;
      userAudioElementRef.current = null;
      if (isUserAudioPlaying && isSame) {
        setIsUserAudioPlaying(false);
        return;
      }
    }

    const audio = new Audio(userAudioUrl);
    userAudioElementRef.current = audio;
    setIsUserAudioPlaying(true);

    audio.onended = () => {
      setIsUserAudioPlaying(false);
      userAudioElementRef.current = null;
    };

    audio.onerror = () => {
      setIsUserAudioPlaying(false);
      userAudioElementRef.current = null;
    };

    audio.play().catch(err => {
      console.error('[Pronunciation] Error playing user audio:', err);
      setIsUserAudioPlaying(false);
    });
  };

  const startListening = async () => {
    if (isListening) {
      stopListening();
      return;
    }

    if (isTransitioning || isProcessing || !selectedDeck) return;

    // Force stop any ongoing TTS & User Audio playback
    window.speechSynthesis.cancel();
    setIsTtsPlaying(false);
    if (userAudioElementRef.current) {
      userAudioElementRef.current.pause();
      userAudioElementRef.current = null;
    }
    setIsUserAudioPlaying(false);

    // Reset feedback, AI tips, recording and UI state for clean retry
    setFeedback(null);
    setAiFeedback(null);
    setUserAudioUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setUserAudioBlob(null);
    setIsNextReady(false);
    setLastTranscript('');
    lastTranscriptRef.current = '';

    // Clear any existing timer before starting
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    setIsRequestingPermission(true);
    isManualStopRef.current = false;
    setTimeLeft(15);

    try {
      // 1. Explicitly request permission first (Mobile requirement)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // 2. Start MediaRecorder for user audio playback feature
      audioChunksRef.current = [];
      try {
        const mimeType = MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : '';
        const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          if (audioChunksRef.current.length > 0) {
            const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
            if (audioBlob.size > 0) {
              const url = URL.createObjectURL(audioBlob);
              setUserAudioUrl(url);
              setUserAudioBlob(audioBlob);
            }
          }
        };

        mediaRecorder.start(100);
      } catch (mrErr) {
        console.warn('[Pronunciation] MediaRecorder start error:', mrErr);
      }

      // 3. Browser compatibility check
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setFeedback('error');
        setLastTranscript('Seu navegador não suporta reconhecimento de voz.');
        setIsRequestingPermission(false);
        return;
      }

      // 4. Request Wake Lock
      await requestWakeLock();

      const recognition = new SpeechRecognition();
      recognition.lang = selectedDeck?.lang || 'en-US';
      recognition.interimResults = true;
      recognition.continuous = true;

      recognition.onstart = () => {
        setIsListening(true);
        setIsRequestingPermission(false);
        setFeedback(null);
        setAiFeedback(null); // Clear previous AI tips when starting new recording
        setLastTranscript('');
        lastTranscriptRef.current = '';

        // Start 15s fixed timer
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              stopListening();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      };

      recognition.onresult = (event: any) => {
        // Ignore results if we are already transitioning or finalizing (Single Source of Truth)
        if (isTransitioning || isFinalizingRef.current || isProcessingFeedback) return;

        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }

        const currentText = (finalTranscript + interimTranscript).trim();
        if (currentText) {
          setLastTranscript(currentText);
          lastTranscriptRef.current = currentText;
        }
      };

      recognition.onerror = (event: any) => {
        console.error("Speech Recognition Error:", event.error);

        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          setFeedback('error');
          setLastTranscript('Permissão de microfone negada.');
        } else if (event.error === 'no-speech') {
          // Ignore
        } else {
          // Only show error if not manually stopped
          if (isListening && !isManualStopRef.current) {
            setFeedback('error');
            setLastTranscript('Erro no reconhecimento.');
          }
        }

        // Don't fully stop UI state on 'no-speech' to allow retries
        if (event.error !== 'no-speech' && !isManualStopRef.current) {
          stopListeningState();
        }
      };

      recognition.onend = () => {
        // Automatically restart if it wasn't a manual stop or transition
        // This handles mobile browsers that stop listening after short pauses
        if (isListening && !isTransitioning && !isManualStopRef.current) {
          try {
            recognition.start();
          } catch (e) {
            // If restart fails, we'll let the timer handle the end
          }
        } else {
          stopListeningState();
        }
      };

      recognitionRef.current = recognition;
      recognition.start();

    } catch (err) {
      console.error("Microphone Permission Error:", err);
      setFeedback('error');
      setLastTranscript('Erro ao acessar o microfone.');
      setIsRequestingPermission(false);
    }
  };

  const stopListeningState = () => {
    setIsListening(false);
    setIsRequestingPermission(false);
    releaseWakeLock();

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.warn('[Pronunciation] MediaRecorder stop error:', e);
      }
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
  };

  const normalizeForComparison = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[.,\/#!$%\^&\*;:{}=\-_~()?]/g, "")
      .split(/\s+/)
      .filter(word => word.length > 2 || /^[çâêîôûàèùé]/.test(word))
      .join(" ")
      .trim();
  };

  const validate = async (transcript: string) => {
    if (isTransitioning || isProcessing || isFinalizingRef.current || isProcessingFeedback) return;
    
    // Lock to prevent concurrent validations/streak updates
    isFinalizingRef.current = true;
    setIsProcessing(true);
    setIsProcessingFeedback(true);
    window.speechSynthesis.cancel(); // Stop any audio during feedback

    const target = selectedDeck?.cards[currentCardIndex].text || '';
    const deckLang = (selectedDeck?.lang || selectedLang || 'en').toLowerCase().split('-')[0];

    // Normalizar números (ex: "vingt" -> "20", "twenty" -> "20") para que tanto numeral quanto palavra por extenso batam 100%
    const targetWithNumbers = normalizeNumbersInText(target, deckLang);
    const transcriptWithNumbers = normalizeNumbersInText(transcript, deckLang);

    const normalizedTarget = normalizeForComparison(targetWithNumbers);
    const normalizedTranscript = normalizeForComparison(transcriptWithNumbers);

    const finalTarget = normalizedTarget.length < 3 ? targetWithNumbers.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_~()?]/g, "").trim() : normalizedTarget;
    const finalTranscript = normalizedTranscript.length < 1 ? transcriptWithNumbers.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_~()?]/g, "").trim() : normalizedTranscript;

    const startTime = Date.now();
    const similarity = getSimilarity(finalTarget, finalTranscript);

    // Dificuldade adaptativa: se o usuário errou este exercício 3x seguidas, considera >= 60% como aprovado
    const currentFailCount = consecutiveErrorsRef.current[currentCardIndex] || 0;
    const hasAdaptiveTolerance = currentFailCount >= 3;
    const isApproved = hasAdaptiveTolerance ? (similarity >= 0.60) : (similarity >= 0.70);

    if (similarity >= 0.90 || (hasAdaptiveTolerance && similarity >= 0.75)) {
      // Resetar contador de erros consecutivos após acertar
      consecutiveErrorsRef.current[currentCardIndex] = 0;

      setFeedback('success');
      soundService.play('success');
      cardAttemptsRef.current.status.push('green');
      cardAttemptsRef.current.transcript.push(transcript);
      playSuccessSound();

      // Streak Increment on successful interaction
      if (user) {
        streakService.validateAndIncrementStreak(user.id, 'pronunciation').then(result => {
           refreshProfile?.(true);
          if (result && result.newlyIncremented) {
            window.dispatchEvent(new CustomEvent('libritui-streak-updated', { detail: result }));
          }
        });
        
        // STATISTICS: Increment pronunciation exercises & pipeline logs
        const pronLang = selectedDeck?.lang?.split('-')[0] || 'all';
        statsService.increment('pronunciation_count', user.id, 1, { langCode: pronLang });
        activeStudyTracker.logExercisePrecision(true, user.id, pronLang);
        activeStudyTracker.logConsistencySession(user.id, pronLang);
        
        import('../services/uniqueWordsService').then(({ uniqueWordsService }) => {
          const targetLang = selectedDeck?.lang?.split('-')[0] || 'en';
          const card = selectedDeck?.cards[currentCardIndex];
          const cardId = card?.id || `pron_${currentCardIndex}`;
          uniqueWordsService.recordExerciseCompletion(
            user.id,
            targetLang,
            'pronunciation',
            cardId,
            card?.text || ''
          );
        });

        // Record exercise progress for study plan & tracking
        exerciseProgressService.recordPractice({
          userId: user.id,
          exerciseType: 'pronunciation',
          langCode: selectedDeck?.lang || selectedLang || 'en',
          level: selectedDeck?.level || selectedLevel || 'A1',
          itemId: String(selectedDeck?.cards[currentCardIndex]?.id || currentCardIndex),
          score: Math.round(similarity * 100),
          isSuccess: true
        }).catch(err => console.warn('[Pronunciation] Record progress err:', err));
      }

      // Stop listening immediately on success
      stopListening();
      setIsNextReady(false);

      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => {
        setIsProcessing(false);
        setIsProcessingFeedback(false);
        setIsTransitioning(false);
        isFinalizingRef.current = false;
        setIsNextReady(true);
      }, 1000);

    } else if (isApproved) {
      // Resetar contador de erros consecutivos após aprovação
      consecutiveErrorsRef.current[currentCardIndex] = 0;

      setFeedback('partial');
      cardAttemptsRef.current.status.push('yellow');
      cardAttemptsRef.current.transcript.push(transcript);
      soundService.play('partial');

      // Streak Increment on partial success
      if (user) {
        streakService.validateAndIncrementStreak(user.id, 'pronunciation').then(result => {
          refreshProfile?.(true);
          if (result && result.newlyIncremented) {
            window.dispatchEvent(new CustomEvent('libritui-streak-updated', { detail: result }));
          }
        });

        // STATISTICS: Increment pronunciation exercises & precision/consistency
        const pronLang = selectedDeck?.lang?.split('-')[0] || profile?.learning_language || 'en';
        statsService.increment('pronunciation_count', user.id, 1, { langCode: pronLang });
        activeStudyTracker.logExercisePrecision(false, user.id, pronLang);
        activeStudyTracker.logConsistencySession(user.id, pronLang);

        exerciseProgressService.recordPractice({
          userId: user.id,
          exerciseType: 'pronunciation',
          langCode: selectedDeck?.lang || selectedLang || 'en',
          level: selectedDeck?.level || selectedLevel || 'A1',
          itemId: String(selectedDeck?.cards[currentCardIndex]?.id || currentCardIndex),
          score: Math.round(similarity * 100),
          isSuccess: true
        }).catch(err => console.warn('[Pronunciation] Record progress err:', err));
      }

      stopListening();
      setIsNextReady(false);

      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => {
        setIsProcessing(false);
        setIsProcessingFeedback(false);
        setIsTransitioning(false);
        isFinalizingRef.current = false;
        setIsNextReady(true);
      }, 1000);

    } else {
      // Incrementar contador de erros para esse card
      consecutiveErrorsRef.current[currentCardIndex] = (consecutiveErrorsRef.current[currentCardIndex] || 0) + 1;

      setFeedback('error');
      soundService.play('error');
      cardAttemptsRef.current.status.push('red');
      cardAttemptsRef.current.transcript.push(transcript);
      if (user) {
        let hash = 0;
        for (let i = 0; i < target.length; i++) {
          hash = ((hash << 5) - hash) + target.charCodeAt(i);
          hash = hash & hash;
        }
        const card = selectedDeck?.cards?.[currentCardIndex];
        const cardId = card?.id ? card.id.toString() : `pron_${Math.abs(hash)}`;
        const targetLang = (selectedDeck?.lang || profile?.learning_language || 'en').split('-')[0].toLowerCase();
        activeStudyTracker.logExercisePrecision(false, user.id, targetLang);
        activeStudyTracker.logConsistencySession(user.id, targetLang);

        import('../services/errorReviewService').then(({ errorReviewService }) => {
          errorReviewService.recordError({
            userId: user.id,
            language: targetLang,
            exerciseType: 'pronunciation',
            sourceOrigin: 'direct_url',
            itemId: cardId,
            itemPayload: card || { phrase: target, translation: card?.translation || '' },
            userInput: transcript
          });
        });

        import('../services/db_manager').then(({ dbManager }) => {
          const uniqueId = `pron_${Math.abs(hash)}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
          dbManager.from('user_pronunciation_corrections').insert({
            id: uniqueId,
            user_id: user.id,
            original_text: target,
            user_speech_text: transcript,
            status_color: 'red',
            target_lang: targetLang,
            created_at: new Date().toISOString()
          }).catch(e => console.warn('[PronunciationPage] user_pronunciation_corrections insert warning:', e));
        });

        exerciseProgressService.recordPractice({
          userId: user.id,
          exerciseType: 'pronunciation',
          langCode: selectedDeck?.lang || selectedLang || 'en',
          level: selectedDeck?.level || selectedLevel || 'A1',
          itemId: cardId,
          score: Math.round(similarity * 100),
          isSuccess: false
        }).catch(err => console.warn('[Pronunciation] Record progress err:', err));
      }
      await generateAIPronunciationFeedback(transcript);
      
      setIsNextReady(false);
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => {
        setIsProcessing(false);
        setIsProcessingFeedback(false);
        setIsTransitioning(false);
        isFinalizingRef.current = false;
        setIsNextReady(true);
      }, 1000);
    }
  };

  const generateAIPronunciationFeedback = async (transcript: string) => {
    const target = selectedDeck?.cards[currentCardIndex].text || '';
    const deckLocale = resolveDeckLocale(selectedDeck?.lang || selectedLang || 'en');
    const targetLangName = getLanguageName(deckLocale.split('-')[0]);

    try {
      const result = await groqService.generatePronunciationFeedback(target, transcript, i18n.language, targetLangName);
      setAiFeedback(result);
    } catch (err) {
      console.error("Groq Pronunciation Error:", err);
    }
  };

  const savePronunciationHistory = () => {
    if (!selectedDeck || cardAttemptsRef.current.status.length === 0) return;
    const currentCard = selectedDeck.cards[currentCardIndex];
    if (!currentCard) return;

    let persistentId = currentCard.id;
    if (selectedDeck.type === 'course' && !isNaN(Number(currentCard.id)) && Number(currentCard.id) < 1000) {
      let hash = 0;
      for (let i = 0; i < currentCard.text.length; i++) {
        hash = ((hash << 5) - hash) + currentCard.text.charCodeAt(i);
        hash = hash & hash;
      }
      persistentId = `r2-${Math.abs(hash)}`;
    }

    const payloadTargetLang = resolveDeckLocale(selectedDeck.lang).split('-')[0];
    const uniqueHistoryId = `${persistentId}_${user?.id || 'guest'}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

    dbManager.from('user_pronunciation_corrections').insert({
      id: uniqueHistoryId,
      user_id: user && !user.isGuest ? user.id : "NULL",
      original_text: currentCard.text,
      user_speech_text: cardAttemptsRef.current.transcript.join(' | '),
      status_color: cardAttemptsRef.current.status.join(','),
      target_lang: payloadTargetLang,
      created_at: getLocalISOString()
    }).catch(e => console.error('[Pronunciation] History save failed:', e));

    cardAttemptsRef.current = { status: [], transcript: [] };
  };

  const nextCard = (bypassLock = false) => {
    if (!bypassLock && isProcessingFeedback) return;
    if (!selectedDeck) return;
    
    savePronunciationHistory();
    resetExerciseState();

    if (currentCardIndex < selectedDeck.cards.length - 1) {
      setCurrentCardIndex(prev => prev + 1);
    } else {
      // Streak increment on level completion
      if (user) {
        streakService.validateAndIncrementStreak(user.id, 'pronunciation');
        refreshProfile?.(true);
      }

      setCurrentCardIndex(0);
      setView('levels');
      return;
    }
  };

  const prevCard = () => {
    if (isProcessingFeedback) return;
    if (!selectedDeck) return;
    
    savePronunciationHistory();
    resetExerciseState();

    if (currentCardIndex > 0) {
      setCurrentCardIndex(prev => prev - 1);
    }
  };

  const currentCard = selectedDeck?.cards[currentCardIndex];

  const currentAudioUrl = useMemo(() => {
    if (!currentCard || !selectedDeck) return null;
    if (selectedDeck.type !== 'course') return null;
    const folder = selectedDeck.lang || selectedLang || 'ingles';
    const lvl = selectedLevel || selectedDeck.level || 'A1';
    const id = currentCard.id || String(currentCardIndex + 1);
    const code = resolveDeckLocale(folder).split('-')[0];
    return getPronunciationAudioUrl(folder, lvl, id, code);
  }, [currentCard, selectedDeck, selectedLang, selectedLevel, currentCardIndex]);

  const handleSpeakWithRate = async (text: string, lang: string) => {
    // 1. If mic is currently listening, stop it
    if (isListening) {
      stopListening();
    }
    
    // 2. Stop user audio recording playback if active
    if (userAudioElementRef.current) {
      try {
        userAudioElementRef.current.pause();
      } catch (e) {}
      userAudioElementRef.current = null;
      setIsUserAudioPlaying(false);
    }

    // 3. Stop and clear previous HTML5 audio to avoid overlap
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch (e) {}
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    if (r2AudioSourceRef.current) {
      try { r2AudioSourceRef.current.stop(); } catch (e) {}
      r2AudioSourceRef.current = null;
    }
    if (r2AudioCtxRef.current) {
      try { r2AudioCtxRef.current.close(); } catch (e) {}
      r2AudioCtxRef.current = null;
    }

    // 4. Toggle stop if TTS is currently playing
    if (isTtsPlaying) {
      try { window.speechSynthesis.cancel(); } catch (e) {}
      try { textToSpeechService.stop(); } catch (e) {}
      setIsTtsPlaying(false);
      return;
    }

    // 5. Play R2 pre-recorded audio or fallback to TTS
    setIsTtsPlaying(true);
    try {
      if (currentAudioUrl) {
        const arrayBuf = await loadPronunciationArrayBuffer(currentAudioUrl);
        if (arrayBuf && arrayBuf.byteLength > 0) {
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          if (AudioContextClass) {
            const ctx = new AudioContextClass();
            r2AudioCtxRef.current = ctx;
            const audioBuffer = await ctx.decodeAudioData(arrayBuf.slice(0));
            const source = ctx.createBufferSource();
            r2AudioSourceRef.current = source;
            source.buffer = audioBuffer;
            source.connect(ctx.destination);
            source.onended = () => {
              setIsTtsPlaying(false);
              r2AudioSourceRef.current = null;
              try { ctx.close(); } catch (e) {}
              r2AudioCtxRef.current = null;
            };
            source.start(0);
            return;
          }
        }
      }
      await textToSpeechService.speak(text, lang, ttsRate);
      setIsTtsPlaying(false);
    } catch (err) {
      console.error("Audio playback error, attempting fallback TTS:", err);
      try {
        await textToSpeechService.speak(text, lang, ttsRate);
      } catch (fallbackErr) {
        console.error("TTS Fallback error:", fallbackErr);
      }
      setIsTtsPlaying(false);
    }
  };

  const isUILocked = isListening || isProcessingFeedback || isProcessing || isTransitioning;
  const isSkipBlocked = isUILocked || feedback === 'success' || feedback === 'partial';
  const isAdvanceSuccess = isNextReady && (feedback === 'success' || feedback === 'partial');
  const isGreenButton = isAdvanceSuccess || feedback === 'error';

  return (
    <div className="space-y-8 animate-in fade-in duration-500 max-w-5xl mx-auto py-6 px-4 md:px-0 h-full flex flex-col pb-24">
      {/* Top Header */}
      <div className="space-y-5 pb-2">
        <AppBackButton
          id="tour-pronunciation-back"
          onClick={handleGoBack}
        />
        <StudyPageHeader tourKey="pronunciation" title={String(safeT('pronunciation.short_title', 'Pronúncia'))} />
      </div>

      {/* Selectors Bar (Idioma, Dificuldade & Modo de Estudo) */}
      <div data-tour="pron-selectors" className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Language Selector */}
        <div className="bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2 block flex items-center gap-2">
            <Globe size={14} className="text-primary" />
            Selecione o Idioma
          </label>
          <div className="relative">
            <select
              value={selectedLang || 'en-US'}
              onChange={(e) => {
                const newLang = e.target.value;
                setSelectedLang(newLang);
                if (activeTab === 'focado') {
                  const currentCode = newLang.split('-')[0].toLowerCase();
                  const personal = personalDecks.find(d => {
                    const dCode = d.lang.split('-')[0].toLowerCase();
                    return dCode === currentCode;
                  });
                  if (personal && personal.cards.length > 0) {
                    setSelectedDeck({ ...personal, cards: shuffleCards(personal.cards), lang: resolveDeckLocale(personal.lang) });
                    setCurrentCardIndex(0);
                  } else {
                    setSelectedDeck(null);
                  }
                } else {
                  handleLevelSelect(selectedLevel || 'A1', newLang);
                }
              }}
              className="w-full appearance-none bg-transparent outline-none pr-10 font-bold text-ink text-sm cursor-pointer"
            >
              {LANGUAGES.map(l => (
                <option key={l.id} value={l.id} className="bg-surface text-ink">
                  {l.flag} {l.name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Difficulty / Level Selector */}
        <div className="bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2 block flex items-center gap-2">
            <GraduationCap size={14} className="text-primary" />
            Selecione a Dificuldade
          </label>
          <div className="relative">
            <select
              value={selectedLevel || 'A1'}
              onChange={(e) => {
                const newLvl = e.target.value;
                setSelectedLevel(newLvl);
                if (activeTab === 'geral') {
                  handleLevelSelect(newLvl, selectedLang || 'en-US');
                }
              }}
              className="w-full appearance-none bg-transparent outline-none pr-10 font-bold text-ink text-sm cursor-pointer"
            >
              {LEVELS.map(lvl => (
                <option key={lvl} value={lvl} className="bg-surface text-ink">
                  {lvl} - {lvl === 'A1' ? 'Iniciante' : lvl === 'A2' ? 'Básico' : lvl === 'B1' ? 'Intermediário' : lvl === 'B2' ? 'Independente' : lvl === 'C1' ? 'Avançado' : 'Domínio'}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Mode Selector (Estudo Geral vs Estudo Focado) */}
        <div data-tour="pron-mode" className="bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2 block flex items-center gap-2">
            <Star size={14} className="text-primary" />
            Modo de Estudo
          </label>
          <div className="relative">
            <select
              value={activeTab}
              onChange={(e) => {
                const mode = e.target.value as 'geral' | 'focado';
                setActiveTab(mode);
                if (mode === 'focado') {
                  const currentCode = (selectedLang || 'en-US').split('-')[0].toLowerCase();
                  const personal = personalDecks.find(d => {
                    const dCode = d.lang.split('-')[0].toLowerCase();
                    return dCode === currentCode;
                  }) || (personalDecks.length > 0 ? personalDecks[0] : null);
                  if (personal && personal.cards.length > 0) {
                    setSelectedDeck({ ...personal, cards: shuffleCards(personal.cards), lang: resolveDeckLocale(personal.lang) });
                    setCurrentCardIndex(0);
                  } else {
                    setSelectedDeck(null);
                  }
                } else {
                  handleLevelSelect(selectedLevel || 'A1', selectedLang || 'en-US');
                }
              }}
              className="w-full appearance-none bg-transparent outline-none pr-10 font-bold text-ink text-sm cursor-pointer"
            >
              <option value="geral" className="bg-surface text-ink">🎓 Estudo Geral</option>
              <option value="focado" className="bg-surface text-ink"><T k="ui.study.focusedMyNotes" /></option>
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Main Pronunciation Content Area */}
      {isLoadingCards ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4 bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] my-6">
          <Loader2 className="animate-spin text-primary" size={56} />
          <p className="text-slate-500 font-normal text-xs uppercase font-mono tracking-[0.14em] animate-pulse">
            {safeT('pronunciation.syncingStudyMaterial', 'Sincronizando material de pronúncia...')}
          </p>
        </div>
      ) : fetchError ? (
        <div className="p-12 text-center bg-tint-rose dark:bg-tint-rose/10 rounded-[28px] border border-tint-rose dark:border-tint-rose/30 my-6 animate-in zoom-in-95">
          <AlertCircle size={48} className="text-bad mx-auto mb-4" />
          <p className="text-bad dark:text-bad font-bold mb-6">{fetchError}</p>
          <button
            onClick={() => handleLevelSelect(selectedLevel || 'A1', selectedLang || 'en-US')}
            className="px-8 py-3 bg-bad text-surface rounded-2xl font-medium text-[13px] active:scale-95 transition-all"
          >
            Tentar Novamente
          </button>
        </div>
      ) : activeTab === 'focado' && (!selectedDeck || selectedDeck.cards.length === 0) ? (
        <div className="p-16 text-center bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] space-y-4 my-6">
          <div className="w-16 h-16 bg-warn/10 rounded-2xl flex items-center justify-center text-warn mx-auto">
            <Star size={32} />
          </div>
          <h3 className="text-xl font-medium text-ink tracking-[-0.02em]"><T k="ui.study.noNotes" /></h3>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            Salve palavras ou frases com o ícone 🧠 durante leituras ou no dicionário para praticar sua pronúncia de forma personalizada aqui.
          </p>
        </div>
      ) : selectedDeck && selectedDeck.cards.length > 0 ? (
        <>
          {/* Counter and Card */}
          <div data-tour="pron-card" className="flex items-center justify-between text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 px-2">
            <span>Card {currentCardIndex + 1} de {selectedDeck.cards.length} ({selectedDeck.name})</span>
            <span>Nível {selectedDeck.level || selectedLevel}</span>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center gap-12">
          <div 
            id="tour-pronunciation-list"
            className={`w-full p-12 rounded-[32px] border-2 transition-all duration-500 flex flex-col items-center text-center gap-6 pronunciation-card-container relative ${feedback === 'success' ? 'bg-ok/15 border-ok scale-[1.02]' :
            feedback === 'partial' ? 'bg-warn/15 border-warn' :
              feedback === 'error' ? 'bg-bad/15 border-bad' :
                'bg-surface border-transparent dark:border-white/[0.06]'
            }`}>
            <div className="flex flex-col items-center gap-2">
              <div className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-ink opacity-60 mb-2">{t('pronunciation.listenAndRepeat')}</div>
              {feedback === 'success' && <Trophy className="text-ok animate-bounce" size={32} />}
              {feedback === 'partial' && <ThumbsUp className="text-warn animate-pulse" size={32} />}
            </div>

            <div className="flex flex-col items-center">
              <h2 className="font-display text-3xl md:text-[42px] font-light tracking-[-0.04em] text-ink leading-[1.1]">
                {renderClickableText(currentCard?.text || '', false)}
              </h2>
            </div>

            {feedback === 'success' && currentCard?.translation && (
              <p className="text-ok dark:text-ok text-sm font-medium -mt-2 animate-in fade-in zoom-in-95">
                {renderClickableText(currentCard.translation, true)}
              </p>
            )}

            {feedback !== 'success' && currentCard?.translation && (
              <p className="text-slate-400 dark:text-slate-500 text-sm font-medium -mt-2">
                {renderClickableText(currentCard.translation, true)}
              </p>
            )}

            {/* Audio Waveform & Emphasis Comparator Overlay */}
            <AudioWaveformComparator
              text={currentCard?.text || ''}
              langLocale={selectedDeck?.lang || selectedLang || 'en-US'}
              userAudioBlob={userAudioBlob}
              userAudioUrl={userAudioUrl}
              isTtsPlaying={isTtsPlaying}
              isUserAudioPlaying={isUserAudioPlaying}
              audioUrl={currentAudioUrl}
            />



            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center gap-4 md:gap-6 flex-wrap justify-center">
                <button
                  onClick={() => setTtsRate(r => Math.max(0.4, r - 0.1))}
                  disabled={isUILocked}
                  className={`p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl text-slate-400 hover:text-primary transition-all active:scale-90 ${isUILocked ? 'opacity-20 cursor-not-allowed' : ''}`}
                  title={safeT('pronunciation.decreaseSpeed', 'Diminuir velocidade')}
                >
                  <Minus size={20} />
                </button>

                {/* Original Native Speaker Audio Button */}
                <button data-tour="pron-native"
                  onClick={() => handleSpeakWithRate(currentCard?.text || '', selectedDeck.lang)}
                  disabled={isUILocked}
                  title="Ouvir voz original (Nativo)"
                  className={`p-5 rounded-[1.8rem] transition-all active:scale-95 flex items-center justify-center gap-2 ${isTtsPlaying ? 'bg-bad text-surface' : (isUILocked ? 'bg-slate-200 dark:bg-slate-800 opacity-20 cursor-not-allowed' : 'bg-primary/10 dark:bg-primary/20 text-primary hover:scale-105')}`}
                >
                  {isTtsPlaying ? <Square size={28} fill="currentColor" /> : <Volume2 size={28} />}
                </button>

                {/* User Recorded Audio Button */}
                {userAudioUrl && (
                  <button data-tour="pron-record"
                    onClick={playUserAudio}
                    disabled={isListening}
                    title={i18nInstance.t('ui.pron.listenMine')}
                    className={`p-5 rounded-[1.8rem] transition-all active:scale-95 flex items-center justify-center gap-2 ${
                      isUserAudioPlaying 
                        ? 'bg-ok text-surface' 
                        : 'bg-warn/10 dark:bg-warn/20 text-warn dark:text-warn hover:scale-105 border border-warn/30'
                    }`}
                  >
                    {isUserAudioPlaying ? <Square size={28} fill="currentColor" /> : <Mic size={28} className="text-warn dark:text-warn" />}
                  </button>
                )}

                <button
                  onClick={() => setTtsRate(r => Math.min(2.0, r + 0.1))}
                  disabled={isUILocked}
                  className={`p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl text-slate-400 hover:text-primary transition-all active:scale-90 ${isUILocked ? 'opacity-20 cursor-not-allowed' : ''}`}
                  title={safeT('pronunciation.increaseSpeed', 'Aumentar velocidade')}
                >
                  <Plus size={20} />
                </button>
              </div>
              <div className="text-[9px] font-normal uppercase font-mono tracking-[0.14em] text-ink opacity-50 flex items-center gap-3">
                <span>{safeT('pronunciation.speed', 'Velocidade')}: {ttsRate.toFixed(1)}x</span>
                {userAudioUrl && (
                  <span className="text-warn dark:text-warn font-bold"><T k="ui.pron.recordingAvailable" /></span>
                )}
              </div>
            </div>

            {feedback === 'partial' && (
              <div className="mt-2 text-warn dark:text-warn font-normal text-[11px] uppercase font-mono tracking-[0.14em] animate-in fade-in">
                Te entendi! Continue praticando.
              </div>
            )}

            {feedback === 'error' && (
              <div className="mt-4 p-4 bg-tint-rose dark:bg-tint-rose/20 rounded-2xl border border-tint-rose dark:border-tint-rose/30 animate-in slide-in-from-top-2 space-y-3">
                <div className="flex items-center gap-2">
                  <p className="text-[9px] font-normal uppercase font-mono text-bad tracking-[0.14em]">{t('pronunciation.didNotUnderstand')}</p>
                  <Brain size={12} className="text-secondary animate-pulse" />
                </div>
                {aiFeedback ? (
                  <p className="text-slate-700 dark:text-slate-300 font-medium text-xs text-left leading-relaxed">{aiFeedback}</p>
                ) : (
                  <p className="text-bad dark:text-bad font-bold text-xs">{t('pronunciation.listenAndTryAgain')}</p>
                )}
              </div>
            )}
          </div>


          <div className="flex items-center gap-6">
            <button
              onClick={prevCard}
              disabled={currentCardIndex === 0 || isUILocked}
              className={`w-[72px] h-[72px] bg-surface rounded-full border border-black/[0.06] dark:border-white/[0.06] text-muted hover:text-ink hover:bg-tint-stone transition-colors group flex flex-col items-center justify-center gap-1 disabled:opacity-25 disabled:cursor-not-allowed ${isUILocked ? 'opacity-25' : ''}`}
            >
              <AnimatedIcon motion="spin"><RotateCcw size={22} strokeWidth={1.6} /></AnimatedIcon>
              <span className="text-[8px] uppercase tracking-[0.14em] font-mono">{safeT('common.back', 'Voltar')}</span>
            </button>

            <button
              onClick={() => nextCard()}
              disabled={isSkipBlocked}
              className={`w-[72px] h-[72px] bg-surface rounded-full border border-black/[0.06] dark:border-white/[0.06] text-muted hover:text-ink hover:bg-tint-stone transition-colors group flex flex-col items-center justify-center gap-1 disabled:opacity-25 disabled:cursor-not-allowed ${isSkipBlocked ? 'opacity-25 cursor-not-allowed' : ''}`}
            >
              <AnimatedIcon motion="slide"><SkipForward size={22} strokeWidth={1.6} /></AnimatedIcon>
              <span className="text-[8px] uppercase tracking-[0.14em] font-mono">{safeT('pronunciation.skip', 'Pular')}</span>
            </button>

            <div className="relative">
              {isListening && !isTransitioning && (
                <div className="absolute inset-0 bg-bad/10 rounded-full animate-pulse" />
              )}
              
              {/* Circular Progress Timer */}
              {isListening && (
                <div className="absolute -inset-2 pointer-events-none rotate-[-90deg]">
                  <svg className="w-28 h-28">
                    <circle
                      cx="56"
                      cy="56"
                      r="50"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="text-slate-100 dark:text-slate-800"
                    />
                    <circle
                      cx="56"
                      cy="56"
                      r="50"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeDasharray="314.159"
                      strokeDashoffset={314.159 * (1 - timeLeft / 15)}
                      className="text-bad transition-all duration-1000 linear"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}

              <button
                onClick={isAdvanceSuccess ? () => nextCard(true) : startListening}
                disabled={isTransitioning || (isProcessing && !isNextReady) || (isRequestingPermission && !isListening)}
                className={`relative w-24 h-24 rounded-full flex items-center justify-center transition-all active:scale-90  ${isListening
                    ? 'bg-bad hover:bg-bad'
                    : isGreenButton
                      ? 'bg-ok hover:bg-ok hover:scale-105'
                      : (isTransitioning || isProcessing)
                        ? 'bg-slate-200 dark:bg-slate-800 opacity-50 cursor-not-allowed'
                        : 'bg-ink hover:scale-105'
                  }`}
              >
                {isListening ? (
                  <div className="flex flex-col items-center justify-center gap-1">
                    <Square size={32} fill="currentColor" className="text-white" />
                    <span className="text-[10px] font-semibold text-white">{timeLeft}s</span>
                  </div>
                ) : isRequestingPermission || isTransitioning || (isProcessing && !isNextReady) ? (
                  <Loader2 className="animate-spin text-slate-400" size={32} />
                ) : isAdvanceSuccess ? (
                  <ArrowRight size={36} className="text-white" />
                ) : (
                  <Mic size={32} className="text-white" />
                )}
              </button>
            </div>
          </div>
        </div>
      </>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 gap-4 bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] my-6">
          <Loader2 className="animate-spin text-primary" size={56} />
          <p className="text-slate-500 font-normal text-xs uppercase font-mono tracking-[0.14em] animate-pulse">
            Carregando exercícios de pronúncia...
          </p>
        </div>
      )}

      {/* Internal Batch Delete Confirmation Modal */}
      {showConfirmBatchDelete && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setShowConfirmBatchDelete(false)} />
          <div className="relative w-full max-w-sm bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-10 text-center">
              <div className="w-20 h-20 bg-tint-rose dark:bg-tint-rose/20 text-bad rounded-3xl flex items-center justify-center mx-auto mb-6">
                <Trash2 size={40} />
              </div>
              <h2 className="text-xl font-medium text-ink tracking-[-0.02em] leading-tight">
                Excluir {selectedCardsForDeletion.size} cards?
              </h2>
              <p className="text-sm font-medium text-slate-500 mt-3 px-2">
                Os cards selecionados serão removidos do seu banco de dados e do cache local.
              </p>
            </div>
            <div className="p-8 bg-slate-50 dark:bg-slate-800/30 flex gap-3">
              <button onClick={() => setShowConfirmBatchDelete(false)} className="text-[13px] font-medium flex-1 h-12 bg-surface dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full border border-black/[0.06] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all">Cancelar</button>
              <button onClick={handleBatchDelete} className="text-[13px] font-medium flex-1 h-12 bg-bad text-white rounded-full hover:opacity-90 active:scale-95 transition-all">{t('pronunciation.confirmDeletion')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Internal Delete Confirmation Modal */}
      {deckToDelete && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setDeckToDelete(null)} />
          <div className="relative w-full max-w-sm bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-10 text-center">
              <div className="w-20 h-20 bg-tint-rose dark:bg-tint-rose/20 text-bad rounded-3xl flex items-center justify-center mx-auto mb-6">
                <Trash2 size={40} />
              </div>
              <h2 className="text-xl font-medium text-ink tracking-[-0.02em] leading-tight">Excluir todas as notas de {LANG_LABEL_MAP[deckToDelete] || deckToDelete}?</h2>
              <p className="text-sm font-medium text-slate-500 mt-3 px-2">{t('pronunciation.removeDeckPermanent')}</p>
            </div>
            <div className="p-8 bg-slate-50 dark:bg-slate-800/30 flex gap-3">
              <button onClick={() => setDeckToDelete(null)} className="text-[13px] font-medium flex-1 h-12 bg-surface dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full border border-black/[0.06] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all">Cancelar</button>
              <button onClick={() => deleteDeck(deckToDelete)} className="text-[13px] font-medium flex-1 h-12 bg-bad text-white rounded-full hover:opacity-90 active:scale-95 transition-all">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PronunciationPage;
